import * as async from "async";
import Backbone from "backbone";
import * as _ from "underscore";

import compositeViewMixin from "compositeViewMixin";
import BooksCollection from "models/BooksCollection";
import * as mustcache from "mustcache";
import PrivateLibraryListView from "views/PrivateLibraryListView";
import PrivateLibraryTagSelectorView from "views/PrivateLibraryTagSelectorView";
import SearchView from "views/SearchView";
import ShelfEditView from "views/ShelfEditView";
import * as walkthrough from "walkthrough";

const PrivateLibraryView = Backbone.View.extend({
  initialize: function (options) {
    _.extend(this, compositeViewMixin);

    this.options = options;
    this.models = options.models;
    this.books = new BooksCollection();

    this.searchQuery = new Backbone.Model({
      query: "",
    });

    this.listenTo(this.books, "change:userBook", (book) => {
      const previous = book.previousAttributes().userBook.tags;
      const current = book.changedAttributes().userBook.tags;
      const toAdd = _.difference(current, previous);
      const toRemove = _.difference(previous, current);

      this.models.bookTags.addTags(toAdd, previous);
      this.models.bookTags.removeTags(toRemove, current);
    });

    this.listenTo(this.books, "remove", (book) => {
      if (book.get("userBook").tags.length === 0) {
        this.models.bookTags.removeDeletedBook();
      } else {
        console.log("weird - removing book which still has tags!?");
      }
    });

    this.listenTo(this.books, "bookRestored", (bookID) => {
      this.models.shelves.addNewBook(bookID);
    });

    this.listenTo(this.books, "deleteAllWithTag", (tag) => {
      this.models.bookTags
        .findWhere({
          name: tag,
        })
        .set({ count: 0 });
    });

    async.parallel(
      [
        (callback) => {
          this.models.bookTags.fetch({
            data: {
              language: this.models.user.get("currentLearningLanguage"),
            },
            success: () => {
              callback();
            },
            error: () => {},
          });
        },
        (callback) => {
          this.models.shelves.reset([]);
          this.models.shelves.fetch({
            data: {
              language: this.models.user.get("currentLearningLanguage"),
            },
            success: () => {
              callback();
            },
            error: () => {},
          });
        },
      ],
      () => {
        const shelfID =
          this.options.shelfID || this.models.shelves.getSelectedShelfID();
        if (shelfID) {
          if (this.models.shelves.get(shelfID)) {
            this.models.shelves.setSelectedShelfID(shelfID);
          } else {
            this.models.shelves.setSelectedShelfID(undefined);
            this.options.appRouter.navigate("/myTexts/queue", {
              trigger: true,
            });
            return;
          }
        } else if (this.options.tag) {
          this.models.bookTags.setActiveTag(this.options.tag);
        }

        const data = {
          language: this.models.user.get("currentLearningLanguage"),
          shelfID: this.models.shelves.getSelectedShelfID(),
          tag: !this.models.shelves.getSelectedShelfID()
            ? this.models.bookTags.activeTag()
            : undefined,
        };
        this.books.fetch({
          data: data,
          success: () => {
            this.listenTo(
              this.models.bookTags,
              "change:activeTag",
              this.fetchBooks.bind(this)
            );
            this.listenTo(
              this.models.shelves,
              "change:shelfID",
              this.fetchBooks.bind(this)
            );
            this.listenTo(
              this.searchQuery,
              "change:query",
              this.fetchBooks.bind(this)
            );

            this.render();
          },
        });
      }
    );
  },
  fetchBooks: function () {
    if (this.suppressFetch) {
      return;
    }
    // use timeout to prevent redundant fetches
    clearTimeout(this.fetchingTimeout);
    this.fetchingTimeout = setTimeout(this._fetchBooks.bind(this), 1);
  },
  _fetchBooks: function () {
    const shelfID = this.models.shelves.getSelectedShelfID();
    this.books.selectionModel.clearSelection();

    const fetchQuery: any = {
      language: this.models.user.get("currentLearningLanguage"),
    };

    if (this.searchQuery.get("query").length > 0) {
      fetchQuery.searchQuery = this.searchQuery.get("query");

      this.suppressFetch = true;
      this.models.bookTags.setActiveTag("queue");
      this.models.shelves.setSelectedShelfID(null);
      this.suppressFetch = false;
    } else {
      if (shelfID) {
        fetchQuery.shelfID = shelfID;
      } else {
        fetchQuery.tag = this.models.bookTags.activeTag();
      }
    }

    this.books.fetch({
      data: fetchQuery,
      success: this.render.bind(this),
      reset: true,
    });
  },
  render: function () {
    const shelfID = this.models.shelves.getSelectedShelfID();
    let activeTag;

    if (shelfID) {
      // Shelf mode
      this.options.appRouter.navigate(
        "/" +
          this.models.user.get("currentLearningLanguage") +
          "/myTexts/shelf/" +
          shelfID
      );
    } else {
      // Normal mode
      activeTag = this.models.bookTags.activeTag();
      this.options.appRouter.navigate(
        "/" +
          this.models.user.get("currentLearningLanguage") +
          "/myTexts/" +
          activeTag
      );
    }

    const data: any = {};

    if (activeTag === "completed") {
      data.booksCompleted = this.books.length;
      data.wordsCompleted = this.books.reduce(
        (memo, book) => memo + (book.get("totalWords") || 0),
        0
      );

      if (this.books.length === 0) {
        data.showCompleted0Message = true;
      } else if (this.books.length === 1) {
        data.showCompleted1Message = true;
      } else if (this.books.length < 5) {
        data.showCompleted2_4Message = true;
      } else {
        data.showCompleted5Message = true;
      }
    }
    if (!walkthrough.hasAchievement("privateTexts")) {
      if (this.books.length === 0) {
        data.noBooks = true;
      } else {
        walkthrough.setAchievement("privateTexts");
      }
    }

    mustcache.render("privateTexts", data, (html) => {
      this.$el.html(html);
      this.delegateEvents(this.events);

      // This is a super hacky way workaround to fix an apparent rendering bug in Safari.
      setTimeout(() => {
        this.$el.find(".flexFixed").css("flex", "0 0 content");
        setTimeout(() => {
          this.$el.find(".flexFixed").css("flex", "0 0 fit-content");
        }, 1);
      }, 1);

      if (!data.noBooks) {
        this.$("[data-subview]").html(
          '<img style="display:block;margin:10px auto 0 auto" src="/content/throbber.gif"/>'
        );

        this.setSubviews({
          libraryListView2: new PrivateLibraryListView({
            el: this.$('[data-subview="libraryListView"]'),
            books: this.books,
            models: this.models,
            tag: activeTag,
            shelfID: shelfID,
          }),
          privateLibraryTagSelectorView: new PrivateLibraryTagSelectorView({
            el: this.$('[data-subview="privateLibraryTagSelectorView"]'),
            models: this.models,
            searchQuery: this.searchQuery,
          }),
          shelfEditView: new ShelfEditView({
            el: this.$('[data-subview="shelfEditView"]'),
            models: this.models,
            books: this.books,
            shelfID: shelfID,
            appRouter: this.options.appRouter,
          }),
          searchView: new SearchView({
            model: this.searchQuery,
            el: this.$('[data-subview="searchView"]'),
          }),
        });
      }
      this.options.renderedCallback();
    });
  },
});

export default PrivateLibraryView;
